import { Button, Dropdown, type MenuProps } from 'antd';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalyticsView } from '../analytics-controls';
import { FilterSmall } from '@shared/assets/icons/components';
import { useGetChannelListChannelListGetQuery } from '@shared/api';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';

import { UrlFilterModal } from './url-filter-modal';
import styles from './analytics-filters.module.scss';

type AnalyticsFiltersProps = {
  view: AnalyticsView;
  channelValues?: number[];
  onChannelChange: (values: number[]) => void;
  pageUrlPatterns: string[];
  onPageUrlPatternsChange: (values: string[]) => void;
};

export const AnalyticsFilters: FC<AnalyticsFiltersProps> = ({
  view,
  channelValues,
  onChannelChange,
  pageUrlPatterns,
  onPageUrlPatternsChange,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'analytics.filters' });
  const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const { data: channels = [], isLoading: isChannelsLoading } =
    useGetChannelListChannelListGetQuery({ organizationId }, { skip: !organizationId });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const isFilterActive =
    Boolean(channelValues?.length) || (view === 'widget' && pageUrlPatterns.length > 0);

  const handleOpenUrlModal = () => {
    setIsDropdownOpen(false);
    setIsUrlModalOpen(true);
  };

  const availableChannels = channels.filter(
    (channel) => view !== 'widget' || channel.type === 'widget',
  );
  const channelMenuItems: MenuProps['items'] = isChannelsLoading
    ? [{ key: 'channels-loading', label: t('loading'), disabled: true }]
    : availableChannels.length
      ? availableChannels.map((channel) => ({
          key: `channel:${channel.id}`,
          label: channel.name,
        }))
      : [{ key: 'channels-empty', label: t('noChannels'), disabled: true }];
  const menuItems: MenuProps['items'] = [
    {
      key: 'channels',
      label: t('channel'),
      children: channelMenuItems,
      popupClassName: styles.dropdownSubmenu,
    },
    ...(view === 'widget' ? [{ key: 'url', label: t('url') }] : []),
  ];
  const selectedChannelKeys = (channelValues ?? []).map((id) => `channel:${id}`);
  const handleChannelSelectionChange: NonNullable<MenuProps['onSelect']> = ({
    key,
    selectedKeys,
  }) => {
    if (!String(key).startsWith('channel:')) return;

    onChannelChange(
      selectedKeys
        .map(String)
        .filter((key) => key.startsWith('channel:'))
        .map((key) => Number(key.slice('channel:'.length)))
        .filter(Number.isFinite),
    );
  };

  return (
    <>
      <Dropdown
        trigger={['click']}
        placement="bottomLeft"
        open={isDropdownOpen}
        overlayClassName={styles.dropdown}
        onOpenChange={(open, info) => {
          if (!open && info.source === 'menu') return;
          setIsDropdownOpen(open);
        }}
        menu={{
          items: menuItems,
          multiple: true,
          selectable: true,
          selectedKeys: selectedChannelKeys,
          onSelect: handleChannelSelectionChange,
          onDeselect: handleChannelSelectionChange,
          onClick: ({ key }) => {
            if (key === 'url') handleOpenUrlModal();
          },
        }}
      >
        <Button
          type="text"
          icon={
            <span className={styles.filterIcon}>
              <FilterSmall />
              {isFilterActive && <span aria-hidden className={styles.activeIndicator} />}
            </span>
          }
          className={styles.trigger}
        >
          {t('title')}
        </Button>
      </Dropdown>

      <UrlFilterModal
        open={isUrlModalOpen}
        value={pageUrlPatterns}
        onCancel={() => setIsUrlModalOpen(false)}
        onApply={(values) => {
          onPageUrlPatternsChange(values);
          setIsUrlModalOpen(false);
        }}
      />
    </>
  );
};
